// ── useTaxPeriod hook ─────────────────────────────────────────────────────
// 분기별 매출 합계 + 의제매입공제 누적 추적
// 경로: users/{uid}/taxPeriods/{year-Q}  (예: 2026-Q2)

import { useEffect, useState, useCallback } from 'react';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from './db';

const periodId = (year, quarter) => `${year}-Q${quarter}`;

/**
 * 한 분기의 TaxPeriod 문서를 읽고 쓰기.
 * 사용자가 입력하는 매출 합계(monthlyRevenue) 등을 저장.
 */
export function useTaxPeriod(user, year, quarter) {
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);

  const id = year && quarter ? periodId(year, quarter) : null;

  useEffect(() => {
    if (!user || !id) { setPeriod(null); setLoading(false); return; }
    setLoading(true);
    const ref = doc(db, 'users', user.uid, 'taxPeriods', id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setPeriod(snap.exists() ? { id: snap.id, ...snap.data() } : { id });
        setLoading(false);
      },
      (e) => {
        console.error('taxPeriod load error:', e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user, id]);

  const updatePeriod = useCallback(async (patch) => {
    if (!user || !id) throw new Error('not signed in or no period');
    const ref = doc(db, 'users', user.uid, 'taxPeriods', id);
    await setDoc(ref, { ...patch, year, quarter, updatedAt: serverTimestamp() }, { merge: true });
  }, [user, id, year, quarter]);

  // 월별 매출 입력 (객체 형태: { '2026-04': 5000000, '2026-05': 6000000, ... })
  const setMonthlyRevenue = useCallback(async (monthMap) => {
    await updatePeriod({ monthlyRevenue: monthMap });
  }, [updatePeriod]);

  // 연 매출 추정 (한도 계산용)
  const setAnnualRevenue = useCallback(async (val) => {
    await updatePeriod({ annualRevenue: val });
  }, [updatePeriod]);

  return {
    period,
    loading,
    updatePeriod,
    setMonthlyRevenue,
    setAnnualRevenue,
  };
}

// 분기 → 해당 월 리스트
export function quarterMonths(year, quarter) {
  if (!year || !quarter) return [];
  const startMonth = (quarter - 1) * 3 + 1;
  return [0, 1, 2].map(i => `${year}-${String(startMonth + i).padStart(2, '0')}`);
}

// 분기 라벨
export function periodLabel(year, quarter) {
  return `${year}년 ${quarter}분기 (${(quarter - 1) * 3 + 1}~${quarter * 3}월)`;
}

// 월별 매출 합계 → 분기 합계
export function quarterRevenueFromMonthly(monthlyRevenue, year, quarter) {
  if (!monthlyRevenue) return 0;
  const months = quarterMonths(year, quarter);
  return months.reduce((sum, m) => sum + (Number(monthlyRevenue[m]) || 0), 0);
}

// 연 매출 추정: 분기 매출 × 4 (단순 추정), 또는 사용자 직접 입력
export function estimateAnnualRevenue({ monthlyRevenue, annualRevenue, year, quarter }) {
  if (annualRevenue && annualRevenue > 0) return annualRevenue;
  if (!monthlyRevenue) return null;
  // 입력된 모든 월 매출 합 / 입력된 월 수 × 12
  const entries = Object.entries(monthlyRevenue).filter(([k]) => k.startsWith(String(year)));
  if (entries.length === 0) return null;
  const sum = entries.reduce((s, [, v]) => s + (Number(v) || 0), 0);
  return Math.round(sum / entries.length * 12);
}
